import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import { format } from 'date-fns';
import { ComplianceCalendarEvent, localEquivalentOfUTC } from 'mapistry-shared';
import React, { type SyntheticEvent, useMemo } from 'react';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { useComplianceCalendarContext } from '../ComplianceCalendarContext';
import {
  getDaySummaryText,
  getDaysWithEvents,
  groupEventsByDay,
  isTodayUtcDay,
} from '../helpers/complianceCalendarHelpers';
import {
  getEventsForSection,
  getSectionEmptyMessage,
  CalendarEventSection,
  CALENDAR_EVENT_SECTIONS,
} from '../helpers/complianceCalendarWeekViewHelpers';
import {
  InspectionsInfoIconContainer,
  StyledSkeleton,
  WeekDayColumnSection,
  WeekDayHeaderDiv,
  WeekDaySummaryDiv,
  WeekViewGridDiv,
  WeekViewSectionAccordion,
} from '../styled';
import { WeekViewSectionContent } from './WeekViewSectionContent';

type ComplianceCalendarWeekViewProps = {
  events: ComplianceCalendarEvent[];
  exclusiveEnd: Date;
  isLoading: boolean;
  navigateToEventUrl: (url: string) => void;
  rangeStart: Date;
  summaryEvents: ComplianceCalendarEvent[];
};

const stopEventPropagation = (event: SyntheticEvent) => event.stopPropagation();

const formatDayHeader = (day: Date) =>
  format(localEquivalentOfUTC(day), 'EEE d').toUpperCase();

export function ComplianceCalendarWeekView({
  events,
  exclusiveEnd,
  isLoading,
  navigateToEventUrl,
  rangeStart,
  summaryEvents,
}: ComplianceCalendarWeekViewProps) {
  const { filters } = useComplianceCalendarContext();
  const visibleSections = CALENDAR_EVENT_SECTIONS.filter((section) =>
    filters.eventTypes.includes(section.type),
  );
  const hasEventTypeFilter =
    visibleSections.length < CALENDAR_EVENT_SECTIONS.length;

  const daysWithEvents = useMemo(
    () =>
      getDaysWithEvents({
        events,
        exclusiveEnd,
        rangeStart,
      }),
    [events, exclusiveEnd, rangeStart],
  );

  const summaryEventsByDay = useMemo(
    () => groupEventsByDay(summaryEvents),
    [summaryEvents],
  );

  const getSummaryContent = (section: CalendarEventSection) => {
    if (section.type !== 'inspection') {
      return section.label;
    }

    return (
      <InspectionsInfoIconContainer>
        {section.label}
        <Tooltip
          title="Inspections events on this calendar are only updated every 2 minutes. Other events are updated in real time."
          onClick={stopEventPropagation}
          onFocus={stopEventPropagation}
        />
      </InspectionsInfoIconContainer>
    );
  };

  return (
    <WeekViewGridDiv
      $columnCount={daysWithEvents.length}
      role="region"
      aria-label="Compliance calendar week view"
    >
      {daysWithEvents.map(({ day, dayKey, events: dayEvents }) => {
        const dayLabel = format(
          localEquivalentOfUTC(day),
          'EEEE, MMMM d, yyyy',
        );
        const isToday = isTodayUtcDay(day);
        const daySummaryEvents = summaryEventsByDay.get(dayKey) ?? dayEvents;
        const isDayEmpty = daySummaryEvents.length === 0;

        let daySummary: React.ReactNode;
        if (isLoading) {
          daySummary = (
            <WeekDaySummaryDiv>
              <VisuallyHidden>Loading day summary...</VisuallyHidden>
              <StyledSkeleton variant="rect" />
            </WeekDaySummaryDiv>
          );
        } else if (isDayEmpty) {
          daySummary = <WeekDaySummaryDiv>No items due</WeekDaySummaryDiv>;
        } else {
          daySummary = (
            <WeekDaySummaryDiv>
              {getDaySummaryText(daySummaryEvents)}
            </WeekDaySummaryDiv>
          );
        }

        return (
          <WeekDayColumnSection
            key={dayKey}
            $isToday={isToday}
            aria-label={dayLabel}
          >
            <WeekDayHeaderDiv>{formatDayHeader(day)}</WeekDayHeaderDiv>
            {daySummary}
            {(isLoading || !isDayEmpty || hasEventTypeFilter) &&
              visibleSections.map((section) => (
                <WeekViewSectionAccordion
                  key={section.type}
                  defaultExpanded
                  variant="condensed"
                  color="secondary"
                  expandIcon={<ArrowDropUpIcon />}
                  summaryContent={getSummaryContent(section)}
                  detailsContent={
                    <WeekViewSectionContent
                      emptyMessage={getSectionEmptyMessage({
                        section,
                        daySummaryEvents,
                      })}
                      events={getEventsForSection(dayEvents, section.type)}
                      isLoading={isLoading}
                      navigateToEventUrl={navigateToEventUrl}
                    />
                  }
                />
              ))}
          </WeekDayColumnSection>
        );
      })}
    </WeekViewGridDiv>
  );
}
